import { IMPROVEMENT_LOOP_NAV_ITEMS } from "../config/improvementLoopNavigation"
import { siteNavigationConfig } from "../config/siteNavigation"
import type { NavigationConfigItem, NavigationFeatureFlag } from "../types"

export type { NavigationFeatureFlag } from "../types"

export interface NavigationCommandNode {
  baseHref?: string
  children?: NavigationCommandNode[]
  featureFlag?: NavigationFeatureFlag
  id: string
  key?: string
  keywords: string[]
  label: string
  parentId: string | null
  requiresAdmin?: boolean
}

export interface FlattenedNavigationCommandNode extends NavigationCommandNode {
  depth: number
  description?: string
  isBranch: boolean
  pathLabels: string[]
}

export const DIRECT_SHORTCUTS_BY_BASE_HREF: Record<string, string> = {
  "/agents": "g a",
  "/benchmarks/stt": "g 1",
  "/benchmarks/tts": "g 2",
  "/dashboard": "g d",
  "/home": "g h",
  "/metrics": "g i",
  "/uploaded": "g o",
  "/alerts": "g m",
  "/personas": "g p",
  "/reports": "g e",
  "/review": "g v",
  "/review/projects": "g v",
  "/simulated": "g r",
  "/scheduled": "g s",
  "/settings": "g x",
  "/simulate": "g n",
  "/templates": "g l",
  "/test-sets": "g t",
}

const createNodeId = (label: string, baseHref?: string) =>
  baseHref
    ? `nav${baseHref}`
    : `section-${label.toLowerCase().replace(/\s+/g, "-")}`

function buildSiteConfigNodes(
  items: NavigationConfigItem[],
  parentId: string | null = null,
): NavigationCommandNode[] {
  return items.map((item) => {
    const hasChildren = Array.isArray(item.subItems) && item.subItems.length > 0
    const nodeId = hasChildren
      ? createNodeId(item.label)
      : createNodeId(item.label, item.base_href)

    return {
      baseHref: hasChildren ? undefined : item.base_href,
      children: hasChildren
        ? item.subItems?.map((subItem) => ({
            baseHref: subItem.base_href,
            featureFlag:
              "featureFlag" in subItem ? subItem.featureFlag : undefined,
            id: createNodeId(subItem.label, subItem.base_href),
            key: DIRECT_SHORTCUTS_BY_BASE_HREF[subItem.base_href],
            keywords: subItem.keywords ?? [subItem.label.toLowerCase()],
            label: subItem.label,
            parentId: nodeId,
          }))
        : undefined,
      featureFlag: item.featureFlag,
      id: nodeId,
      key: hasChildren
        ? undefined
        : DIRECT_SHORTCUTS_BY_BASE_HREF[item.base_href],
      keywords: item.keywords ?? [item.label.toLowerCase()],
      label: item.label,
      parentId,
    }
  })
}

/**
 * Templates and Scheduled have no row in the loop nav — the Simulated page
 * owns both there — so that tree carries them itself. The flat nav still lists
 * both under Simulated, where a second node would double them.
 */
const SIMULATIONS_PAGE_DESTINATIONS: NavigationCommandNode[] = [
  {
    baseHref: "/scheduled",
    id: createNodeId("Scheduled", "/scheduled"),
    key: DIRECT_SHORTCUTS_BY_BASE_HREF["/scheduled"],
    keywords: ["scheduled", "runs"],
    label: "Scheduled",
    parentId: null,
  },
  {
    baseHref: "/templates",
    id: createNodeId("Templates", "/templates"),
    key: DIRECT_SHORTCUTS_BY_BASE_HREF["/templates"],
    keywords: ["templates", "test", "evaluate", "coval"],
    label: "Templates",
    parentId: null,
  },
]

/**
 * The palette's tree is the sidebar's, plus the entries that have no sidebar
 * row — Simulate and Settings always, and whatever `extraNodes` the caller's
 * sidebar leaves out.
 *
 * It takes the nav items rather than reading the config directly so the palette
 * follows whichever sidebar the viewer has: a `g` shortcut that walked a
 * section the viewer cannot see would be a path to nowhere.
 */
function buildNavigationCommandTree(
  navItems: NavigationConfigItem[],
  extraNodes: NavigationCommandNode[] = [],
): NavigationCommandNode[] {
  return [
    {
      baseHref: "/simulate",
      id: createNodeId("Simulate", "/simulate"),
      key: DIRECT_SHORTCUTS_BY_BASE_HREF["/simulate"],
      keywords: ["simulate", "new run", "launch simulation"],
      label: "Simulate",
      parentId: null,
    },
    ...buildSiteConfigNodes(navItems),
    ...extraNodes,
    {
      baseHref: "/settings",
      id: createNodeId("Settings", "/settings"),
      key: DIRECT_SHORTCUTS_BY_BASE_HREF["/settings"],
      keywords: ["settings", "preferences", "integrations", "configuration"],
      label: "Settings",
      parentId: null,
    },
    {
      baseHref: "/admin",
      id: createNodeId("Admin", "/admin"),
      key: "g z",
      keywords: ["admin"],
      label: "Admin",
      parentId: null,
      requiresAdmin: true,
    },
  ]
}

export const NAVIGATION_COMMAND_TREE: NavigationCommandNode[] =
  buildNavigationCommandTree(siteNavigationConfig.navItems)

export const IMPROVEMENT_LOOP_NAVIGATION_COMMAND_TREE: NavigationCommandNode[] =
  buildNavigationCommandTree(
    IMPROVEMENT_LOOP_NAV_ITEMS,
    SIMULATIONS_PAGE_DESTINATIONS,
  )

export function flattenNavigationCommandTree(
  nodes: NavigationCommandNode[],
  parentLabels: string[] = [],
): FlattenedNavigationCommandNode[] {
  return nodes.flatMap((node) => {
    const pathLabels = [...parentLabels, node.label]
    const flattenedNode: FlattenedNavigationCommandNode = {
      ...node,
      depth: parentLabels.length,
      description: parentLabels[parentLabels.length - 1],
      isBranch: (node.children?.length ?? 0) > 0,
      pathLabels,
    }

    return [
      flattenedNode,
      ...(node.children
        ? flattenNavigationCommandTree(node.children, pathLabels)
        : []),
    ]
  })
}

export function filterNavigationCommandTree(
  nodes: NavigationCommandNode[],
  options: {
    canAccess: (baseHref: string) => boolean
    featureFlagStatus: Partial<Record<NavigationFeatureFlag, boolean>>
    hasAdminAccess: boolean
  },
): NavigationCommandNode[] {
  return nodes.flatMap((node) => {
    if (node.requiresAdmin && !options.hasAdminAccess) {
      return []
    }

    if (
      node.featureFlag &&
      options.featureFlagStatus[node.featureFlag] !== true
    ) {
      return []
    }

    const children = node.children
      ? filterNavigationCommandTree(node.children, options)
      : undefined

    if (node.baseHref && !options.canAccess(node.baseHref)) {
      return children?.length ? [{ ...node, children }] : []
    }

    if (!node.baseHref && children?.length === 0) {
      return []
    }

    return [{ ...node, children }]
  })
}
